import React from 'react';
import { X, Package, Calendar, User, FileText, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { POrderLineWithHeaderDto } from '../../../../../types/purchaseOrderLines';
import { purchaseOrderLinesService } from '../../../../../services/purchaseOrderLinesService';
import { StatusBadge } from '../../../../common/StatusBadge';

interface PurchaseOrderLineDetailsModalProps {
  line: POrderLineWithHeaderDto;
  onClose: () => void;
}

export const PurchaseOrderLineDetailsModal: React.FC<PurchaseOrderLineDetailsModalProps> = ({
  line,
  onClose,
}) => {
  const { t } = useTranslation();
  const completionPercentage = purchaseOrderLinesService.calculateCompletionPercentage(
    line.quantity,
    line.quantityDelivered
  );


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6 text-purple-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('manager.modules.purchaseOrderLines.modal.title', {}, 'Bestillingslinje detaljer')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('manager.modules.purchaseOrderLines.modal.subtitle', { '0': line.orderNoERP, '1': line.lineNoERP || line.lineNo }, `Ordre ${line.orderNoERP} - Linje ${line.lineNoERP || line.lineNo}`)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {/* Order Header Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.purchaseOrderLines.modal.orderInfoHeading', {}, 'Ordreinformasjon')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.orderNoWms', {}, 'Ordrenummer (WMS)')}
                </label>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {line.orderNo}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.orderNoErp', {}, 'Ordrenummer (ERP)')}
                </label>
                <div className="text-sm font-semibold text-gray-900 mt-1">
                  {line.orderNoERP}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.columns.orderStatus', {}, 'Ordrestatus')}
                </label>
                <div className="mt-1">
                  <StatusBadge value={line.orderStatus} statusType={1} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.columns.supplierName', {}, 'Leverandør')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  <div className="font-medium">{line.supplierNo}</div>
                  <div className="text-xs text-gray-600">{line.supplierName}</div>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.columns.orderDate', {}, 'Ordredato')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDate(line.orderDate)}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.expectedDelivery', {}, 'Forventet levering')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDate(line.orderDeliveryDate)}
                </div>
              </div>
              {line.supplierOrderNo && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.supplierOrderNo', {}, 'Leverandørens ordrenr')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">
                    {line.supplierOrderNo}
                  </div>
                </div>
              )}
              {line.yourRef && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.yourRef', {}, 'Deres ref.')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.yourRef}</div>
                </div>
              )}
              {line.ourRef && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.ourRef', {}, 'Vår ref.')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.ourRef}</div>
                </div>
              )}
            </div>
          </div>

          {/* Line Details Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.purchaseOrderLines.modal.lineDetailsHeading', {}, 'Linjedetaljer')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left column */}
              <div className="space-y-4 bg-white border border-gray-200 p-4 rounded-lg">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.lineNoWms', {}, 'Linjenummer (WMS)')}
                  </label>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {line.lineNo}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.lineNoErp', {}, 'Linjenummer (ERP)')}
                  </label>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {line.lineNoERP || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.columns.lineStatus', {}, 'Linjestatus')}
                  </label>
                  <div className="mt-1">
                    <StatusBadge value={line.lineStatus} statusType={1} />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('warehouse.general.itemNumber', {}, 'Varenummer')}
                  </label>
                  <div className="text-base font-bold text-blue-600 mt-1">
                    {line.itemNo}
                  </div>
                </div>
                {line.itemBarCode && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      {t('manager.modules.purchaseOrderLines.columns.itemBarCode', {}, 'Strekkode')}
                    </label>
                    <div className="text-sm text-gray-900 mt-1">{line.itemBarCode}</div>
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('common.fields.description', {}, 'Beskrivelse')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">
                    {line.description1 || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4 bg-white border border-gray-200 p-4 rounded-lg">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.orderedQuantity', {}, 'Bestilt antall')}
                  </label>
                  <div className="text-2xl font-bold text-gray-900 mt-1">
                    {purchaseOrderLinesService.formatQuantity(line.quantity)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.deliveredQuantity', {}, 'Levert antall')}
                  </label>
                  <div className="text-2xl font-bold text-green-600 mt-1">
                    {purchaseOrderLinesService.formatQuantity(line.quantityDelivered)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.remainingQuantity', {}, 'Gjenstående antall')}
                  </label>
                  <div className="text-2xl font-bold text-orange-600 mt-1">
                    {purchaseOrderLinesService.formatQuantity(line.remainingQuantity)}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.columns.completionPct', {}, 'Framdrift')}
                  </label>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full ${
                          completionPercentage === 100
                            ? 'bg-green-500'
                            : completionPercentage > 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        } transition-all`}
                        style={{ width: `${completionPercentage}%` }}
                      />
                    </div>
                    <span className="text-lg font-bold text-gray-700 min-w-[60px]">
                      {completionPercentage}%
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">{t('manager.modules.purchaseOrderLines.columns.itemUnitCode', {}, 'Enhet')}</label>
                  <div className="text-sm text-gray-900 mt-1">
                    {line.itemUnitTxt || line.itemUnitCode || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('manager.modules.purchaseOrderLines.modal.additionalInfoHeading', {}, 'Tilleggsinformasjon')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
              {line.price !== null && line.price !== undefined && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">{t('manager.modules.purchaseOrderLines.columns.price', {}, 'Pris')}</label>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {purchaseOrderLinesService.formatCurrency(line.price)}
                  </div>
                </div>
              )}
              {line.discount !== null && line.discount !== undefined && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.discount', {}, 'Rabatt')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.discount}%</div>
                </div>
              )}
              {line.warehouse && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.warehouse', {}, 'Lager')}</label>
                  <div className="text-sm text-gray-900 mt-1">{line.warehouse}</div>
                </div>
              )}
              {line.deliveryDate && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.deliveryDateLine', {}, 'Leveringsdato (linje)')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">
                    {purchaseOrderLinesService.formatDate(line.deliveryDate)}
                  </div>
                </div>
              )}
              {line.stdLocationName && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.standardLocation', {}, 'Standardlokasjon')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.stdLocationName}</div>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.batchControlled', {}, 'Batch-styrt')}
                </label>
                <div className="mt-1">
                  {line.batchItemYesNo ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('manager.modules.purchaseOrderLines.modal.yes', {}, 'Ja')}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm">{t('manager.modules.purchaseOrderLines.modal.no', {}, 'Nei')}</span>
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.serialControlled', {}, 'Serienummer-styrt')}
                </label>
                <div className="mt-1">
                  {line.serialNoItemYesNo ? (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('manager.modules.purchaseOrderLines.modal.yes', {}, 'Ja')}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-gray-400">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm">{t('manager.modules.purchaseOrderLines.modal.no', {}, 'Nei')}</span>
                    </span>
                  )}
                </div>
              </div>
              {line.itemBatchNo && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.modal.batchNumber', {}, 'Batch-nummer')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.itemBatchNo}</div>
                </div>
              )}
              {line.itemGroup1 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.columns.itemGroup1', {}, 'Varegruppe 1')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.itemGroup1}</div>
                </div>
              )}
              {line.itemGroup2 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.columns.itemGroup2', {}, 'Varegruppe 2')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.itemGroup2}</div>
                </div>
              )}
              {line.itemGroup3 && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">
                    {t('manager.modules.purchaseOrderLines.columns.itemGroup3', {}, 'Varegruppe 3')}
                  </label>
                  <div className="text-sm text-gray-900 mt-1">{line.itemGroup3}</div>
                </div>
              )}
            </div>
          </div>

          {/* Free Fields (if any) */}
          {(line.orderLineFreeField1 ||
            line.orderLineFreeField2 ||
            line.orderLineFreeField3) && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('manager.modules.purchaseOrderLines.modal.freeFieldsHeading', {}, 'Frie felt')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                {line.orderLineFreeField1 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      {t('manager.modules.purchaseOrderLines.modal.freeField1', {}, 'Fri felt 1')}
                    </label>
                    <div className="text-sm text-gray-900 mt-1">
                      {line.orderLineFreeField1}
                    </div>
                  </div>
                )}
                {line.orderLineFreeField2 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      {t('manager.modules.purchaseOrderLines.modal.freeField2', {}, 'Fri felt 2')}
                    </label>
                    <div className="text-sm text-gray-900 mt-1">
                      {line.orderLineFreeField2}
                    </div>
                  </div>
                )}
                {line.orderLineFreeField3 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase">
                      {t('manager.modules.purchaseOrderLines.modal.freeField3', {}, 'Fri felt 3')}
                    </label>
                    <div className="text-sm text-gray-900 mt-1">
                      {line.orderLineFreeField3}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Information */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.purchaseOrderLines.modal.auditHeading', {}, 'Sporingsinformasjon')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.orderCreated', {}, 'Ordre opprettet')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDateTime(line.orderCreated)}
                  {line.orderCreatedBy && (
                    <span className="text-xs text-gray-600 block">
                      {t('manager.modules.purchaseOrderLines.modal.by', { '0': line.orderCreatedBy }, `av ${line.orderCreatedBy}`)}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.orderLastUpdate', {}, 'Ordre sist oppdatert')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDateTime(line.orderLastUpdate)}
                  {line.orderUpdatedBy && (
                    <span className="text-xs text-gray-600 block">
                      {t('manager.modules.purchaseOrderLines.modal.by', { '0': line.orderUpdatedBy }, `av ${line.orderUpdatedBy}`)}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.lineCreated', {}, 'Linje opprettet')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDateTime(line.lineCreated)}
                  {line.lineCreatedBy && (
                    <span className="text-xs text-gray-600 block">
                      {t('manager.modules.purchaseOrderLines.modal.by', { '0': line.lineCreatedBy }, `av ${line.lineCreatedBy}`)}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">
                  {t('manager.modules.purchaseOrderLines.modal.lineLastUpdate', {}, 'Linje sist oppdatert')}
                </label>
                <div className="text-sm text-gray-900 mt-1">
                  {purchaseOrderLinesService.formatDateTime(line.lineLastUpdate)}
                  {line.lineUpdatedBy && (
                    <span className="text-xs text-gray-600 block">
                      {t('manager.modules.purchaseOrderLines.modal.by', { '0': line.lineUpdatedBy }, `av ${line.lineUpdatedBy}`)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
          >
            {t('common.buttons.close', {}, 'Lukk')}
          </button>
        </div>
      </div>
    </div>
  );
};
